import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import CategoryMultiPicker from "@/components/CategoryMultiPicker";
import LogoUploader from "@/components/LogoUploader";
import GalleryUploader from "@/components/GalleryUploader";
import HoursEditor from "@/components/HoursEditor";
import { SOCIAL_PLATFORMS } from "@/components/SocialLinks";
import LoyaltyProgramCard from "@/components/LoyaltyProgramCard";
import { findGroupForCategory } from "@/data/taxonomy";
import { toast } from "sonner";

export default function BusinessEdit() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [form, setForm] = useState({
    business_name: "",
    business_address: "",
    business_city: "",
    business_state: "",
    business_zip: "",
    description: "",
    website: "",
    phone: "",
    contact_email: "",
    date_established: "",
    categories: [],
    logo_data_url: "",
    socials: {},
    hours: {},
    opentable_rid: "",
    price_tier: "",
    license_number: "",
    license_type: "",
    license_state: "",
    license_verification_url: "",
  });

  useEffect(() => {
    if (!user) return;
    setForm({
      business_name: user.business_name || "",
      business_address: user.business_address || "",
      business_city: user.business_city || "",
      business_state: user.business_state || "",
      business_zip: user.business_zip || "",
      description: user.description || "",
      website: user.website || "",
      phone: user.phone || "",
      contact_email: user.contact_email || "",
      date_established: user.date_established || "",
      categories: user.categories || (user.category ? [user.category] : []),
      logo_data_url: user.logo_data_url || "",
      socials: user.socials || {},
      hours: user.hours || {},
      opentable_rid: user.opentable_rid || "",
      price_tier: user.price_tier || "",
      license_number: user.license_number || "",
      license_type: user.license_type || "",
      license_state: user.license_state || "",
      license_verification_url: user.license_verification_url || "",
    });
    setGallery(user.gallery || []);
    setLoading(false);
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.categories?.length) {
        payload.category_groups = Array.from(
          new Set(payload.categories.map((c) => findGroupForCategory(c) || c))
        );
      } else {
        payload.category_groups = [];
      }
      await api.put("/me/business", payload);
      await refresh();
      toast.success("Listing updated");
      navigate("/dashboard/business");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) {
    return <div className="max-w-3xl mx-auto px-6 py-12 text-gray-500">Loading…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-12">
      <Link
        to="/dashboard/business"
        className="inline-flex items-center text-sm text-gray-500 hover:text-[#B31E1E] mb-6"
        data-testid="edit-back-link"
      >
        <ArrowLeft size={14} className="mr-1" /> Back to business console
      </Link>
      <div className="overline text-gray-500">Edit listing</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">
        {form.business_name || "Your business"}
      </h1>
      <p className="text-gray-600 mt-2">
        Update your public profile, contact info, categories and logo. Login email is managed separately for security.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-10">
        <Card className="rounded-md border border-border p-6">
          <h3 className="font-display text-2xl">Logo</h3>
          <p className="text-sm text-gray-500 mt-1">Shown on your business page and directory listing.</p>
          <div className="mt-4">
            <LogoUploader
              value={form.logo_data_url}
              onChange={(v) => setForm((f) => ({ ...f, logo_data_url: v }))}
            />
          </div>
        </Card>

        <Card className="rounded-md border border-border p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-display text-2xl">Gallery of your work</h3>
              <p className="text-sm text-gray-500 mt-1">
                Upload photos of completed jobs, your shop, or your team. Shown publicly on your business page.
              </p>
            </div>
            {!user?.subscription_active && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                Pro feature
              </span>
            )}
          </div>
          <div className="mt-4">
            {user?.subscription_active ? (
              <GalleryUploader images={gallery} onChange={setGallery} />
            ) : (
              <div className="border border-dashed border-amber-300 bg-amber-50/50 rounded-md p-5 text-sm" data-testid="gallery-locked">
                <p className="text-gray-800">
                  Uploading work photos is part of servrevu Pro. Subscribers can showcase up to 12 photos
                  with captions on their public listing.
                </p>
                <Link to="/subscription">
                  <Button type="button" className="mt-4 rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="gallery-upgrade-cta">
                    Subscribe to upload photos →
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </Card>

        <Card className="rounded-md border border-border p-6 space-y-5">
          <h3 className="font-display text-2xl">Basics</h3>
          <div>
            <Label htmlFor="ed-name">Business name</Label>
            <Input id="ed-name" value={form.business_name} onChange={set("business_name")} className="rounded-md mt-1" data-testid="edit-business-name" required />
          </div>
          <div>
            <Label>Categories (up to 10)</Label>
            <div className="mt-2">
              <CategoryMultiPicker
                value={form.categories}
                onChange={(cats) => setForm((f) => ({ ...f, categories: cats }))}
                testIdPrefix="edit-category"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="ed-desc">About / description</Label>
            <Textarea
              id="ed-desc"
              rows={4}
              value={form.description}
              onChange={set("description")}
              placeholder="What do you do? What makes you different?"
              className="rounded-md mt-1"
              data-testid="edit-description"
            />
          </div>
          <div>
            <Label htmlFor="ed-date">Year established</Label>
            <Input
              id="ed-date"
              type="number"
              min="1850"
              max="2100"
              step="1"
              placeholder="2015"
              value={(form.date_established || "").slice(0, 4)}
              onChange={set("date_established")}
              className="rounded-md mt-1 max-w-xs"
              data-testid="edit-date-established"
            />
          </div>
          <div>
            <Label>Price tier</Label>
            <p className="text-xs text-gray-500 mt-1">Helps customers gauge how much to budget. Shown next to your city on directory cards.</p>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="edit-price-tier">
              {["", "$", "$$", "$$$", "$$$$"].map((t) => (
                <button
                  key={t || "none"}
                  type="button"
                  onClick={() => setForm({ ...form, price_tier: t })}
                  className={`px-4 py-2 rounded-[35px] text-sm font-semibold border transition-colors ${
                    form.price_tier === t
                      ? "bg-[#B31E1E] text-white border-[#B31E1E]"
                      : "bg-white text-gray-700 border-border hover:bg-gray-50"
                  }`}
                  data-testid={`edit-price-tier-${t || "none"}`}
                >
                  {t || "—"}
                </button>
              ))}
            </div>
          </div>
        </Card>

        <Card className="rounded-md border border-border p-6 space-y-5">
          <h3 className="font-display text-2xl">Address</h3>
          <div>
            <Label htmlFor="ed-addr">Street address</Label>
            <Input id="ed-addr" value={form.business_address} onChange={set("business_address")} className="rounded-md mt-1" data-testid="edit-address" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="ed-city">City</Label>
              <Input id="ed-city" value={form.business_city} onChange={set("business_city")} className="rounded-md mt-1" data-testid="edit-city" />
            </div>
            <div>
              <Label htmlFor="ed-state">State</Label>
              <Input id="ed-state" value={form.business_state} onChange={set("business_state")} placeholder="CA" className="rounded-md mt-1" data-testid="edit-state" />
            </div>
            <div>
              <Label htmlFor="ed-zip">ZIP / Postal code</Label>
              <Input id="ed-zip" value={form.business_zip} onChange={set("business_zip")} placeholder="94110" className="rounded-md mt-1" data-testid="edit-zip" />
            </div>
          </div>
        </Card>

        <Card className="rounded-md border border-border p-6 space-y-5">
          <h3 className="font-display text-2xl">Contact</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ed-phone">Phone</Label>
              <Input id="ed-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" className="rounded-md mt-1" data-testid="edit-phone" />
            </div>
            <div>
              <Label htmlFor="ed-cemail">Public contact email</Label>
              <Input id="ed-cemail" type="email" value={form.contact_email} onChange={set("contact_email")} placeholder="hello@yourbusiness.com" className="rounded-md mt-1" data-testid="edit-contact-email" />
            </div>
          </div>
          <div>
            <Label htmlFor="ed-web">Website</Label>
            <Input id="ed-web" value={form.website} onChange={set("website")} placeholder="https://" className="rounded-md mt-1" data-testid="edit-website" />
          </div>
        </Card>

        <Card className="rounded-md border border-border p-6 space-y-5">
          <div>
            <h3 className="font-display text-2xl">Hours of operation</h3>
            <p className="text-sm text-gray-500 mt-1">Displayed on your public profile with a live "Open now" badge.</p>
          </div>
          <HoursEditor
            value={form.hours}
            onChange={(h) => setForm((f) => ({ ...f, hours: h }))}
          />
        </Card>

        <Card className="rounded-md border border-border p-6 space-y-4">
          <div>
            <h3 className="font-display text-2xl">Reservations (OpenTable)</h3>
            <p className="text-sm text-gray-500 mt-1">
              If your restaurant is on OpenTable, paste your restaurant ID (RID) below to show a reservation widget on your profile.
              Find your RID in the URL of your OpenTable listing — e.g. <code className="font-mono text-xs bg-gray-100 px-1 rounded">opentable.com/r/the-french-laundry-yountville?rid=<strong>1</strong></code>.
            </p>
          </div>
          <div>
            <Label htmlFor="ed-otrid">OpenTable restaurant ID</Label>
            <Input
              id="ed-otrid"
              inputMode="numeric"
              placeholder="e.g. 12345"
              value={form.opentable_rid}
              onChange={(e) => setForm({ ...form, opentable_rid: e.target.value.replace(/\D/g, "").slice(0, 12) })}
              className="rounded-md mt-1 max-w-xs font-mono"
              data-testid="edit-opentable-rid"
            />
            <p className="text-[11px] text-gray-500 mt-1.5">Numbers only. Leave blank to hide the reservation widget.</p>
          </div>
        </Card>

        <Card className="rounded-md border border-border p-6 space-y-4" data-testid="edit-license-card">
          <div>
            <h3 className="font-display text-2xl">Trade / contractor license</h3>
            <p className="text-sm text-gray-500 mt-1">
              Optional but recommended for contractors, electricians, plumbers, HVAC, roofers, etc.
              A verified license number builds trust — customers can look you up on your state licensing board.
              Common examples: Arizona <span className="font-mono">ROC</span>, California <span className="font-mono">CSLB</span>, Texas <span className="font-mono">TDLR</span>.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="sm:col-span-1">
              <Label htmlFor="ed-lic-type">License type</Label>
              <Input
                id="ed-lic-type"
                value={form.license_type}
                onChange={set("license_type")}
                placeholder="ROC"
                maxLength={20}
                className="rounded-md mt-1 font-mono"
                data-testid="edit-license-type"
              />
              <p className="text-[11px] text-gray-500 mt-1">e.g. ROC, CSLB</p>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="ed-lic-num">License number</Label>
              <Input
                id="ed-lic-num"
                value={form.license_number}
                onChange={set("license_number")}
                placeholder="123456"
                maxLength={40}
                className="rounded-md mt-1 font-mono"
                data-testid="edit-license-number"
              />
              <p className="text-[11px] text-gray-500 mt-1">Alphanumeric + dashes only.</p>
            </div>
            <div className="sm:col-span-1">
              <Label htmlFor="ed-lic-state">State</Label>
              <Input
                id="ed-lic-state"
                value={form.license_state}
                onChange={(e) => setForm({ ...form, license_state: e.target.value.toUpperCase().slice(0, 4) })}
                placeholder="AZ"
                maxLength={4}
                className="rounded-md mt-1 font-mono uppercase"
                data-testid="edit-license-state"
              />
              <p className="text-[11px] text-gray-500 mt-1">2-letter code</p>
            </div>
          </div>
          <div>
            <Label htmlFor="ed-lic-url">Verification URL (optional)</Label>
            <Input
              id="ed-lic-url"
              type="url"
              value={form.license_verification_url}
              onChange={set("license_verification_url")}
              placeholder="https://roc.az.gov/licensee-search"
              className="rounded-md mt-1"
              data-testid="edit-license-url"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Paste the URL of your state licensing board&apos;s lookup tool. Customers will see a
              <span className="font-semibold"> Verify license</span> link on your profile.
            </p>
          </div>
        </Card>

        <Card className="rounded-[24px] p-6 space-y-4 border border-border bg-white">
          <div>
            <h3 className="font-display text-xl">Social media</h3>
            <p className="text-xs text-gray-500 mt-1">Paste the full URL to each profile. Empty fields are hidden on your public page.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {SOCIAL_PLATFORMS.map((p) => (
              <div key={p.key}>
                <Label htmlFor={`ed-social-${p.key}`}>{p.label}</Label>
                <Input
                  id={`ed-social-${p.key}`}
                  value={form.socials?.[p.key] || ""}
                  onChange={(e) => setForm({ ...form, socials: { ...form.socials, [p.key]: e.target.value } })}
                  placeholder={p.placeholder}
                  className="rounded-md mt-1"
                  data-testid={`edit-social-${p.key}`}
                />
              </div>
            ))}
          </div>
        </Card>

        <LoyaltyProgramCard marketingActive={!!user?.marketing_active} />

        <div className="flex gap-3">
          <Button
            type="submit"
            disabled={saving}
            className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717] h-11 px-6"
            data-testid="edit-save-button"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
          <Link to="/dashboard/business">
            <Button type="button" variant="ghost" className="rounded-[35px] h-11 px-6">Cancel</Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
